import { useEffect, useRef, useState } from "react";
import EmojiPicker from "../EmojiPicker";
import GifPicker from "../GifPicker";
import { Spinner } from "../PageLoader";
import { brandGradient, Popover, CloseIcon, PhotoIcon, CheckIcon, ReplyIcon, EditIcon, youtubeId, splitPreviewGlyph, PreviewIcon, CHAT_COL } from "./ui";

const URL_RE = /https?:\/\/[^\s]+/;

/** WhatsApp-style compose-time link preview shown above the input while typing a
 *  URL. Rich (thumbnail + title) for YouTube via oEmbed; domain + URL otherwise. */
function LinkPreviewCard({ url, onDismiss }) {
  const ytId = youtubeId(url);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!ytId) return;
    let active = true;
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d) setMeta({ title: d.title, author: d.author_name });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [url, ytId]);

  let domain = "";
  try {
    domain = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    /* ignore */
  }
  const thumb = ytId ? `https://i.ytimg.com/vi/${ytId}/mqdefault.jpg` : null;

  return (
    <div className="mb-2 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/90 p-2 pr-2">
      {thumb ? (
        <img src={thumb} alt="" className="h-12 w-[72px] shrink-0 rounded-lg object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
      ) : (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface text-slate-400 ring-1 ring-slate-200">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>
        </span>
      )}
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-bold text-slate-800">{meta?.title || domain || url}</span>
        <span className="block truncate text-[11px] text-slate-400">{ytId ? (meta?.author ? `${meta.author} · ${domain}` : domain || "youtube.com") : url}</span>
      </div>
      <button type="button" onClick={onDismiss} aria-label="Remove preview" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-600">
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

const CameraIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h3l1.5-2h7L17 8h3a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z" />
    <circle cx="12" cy="13" r="3.2" />
  </svg>
);

/** Video (film) attach glyph. */
const VideoGlyph = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="13" height="12" rx="2.5" />
    <path d="M16 10l4.2-2.4a.6.6 0 01.9.52v7.76a.6.6 0 01-.9.52L16 14" />
  </svg>
);

/** Audio (music note) attach glyph. */
const AudioGlyph = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V6l10-2v12" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="16" cy="16" r="3" />
  </svg>
);

/** Document attach glyph. */
const DocGlyph = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3h6l4 4v12.5A1.5 1.5 0 0116.5 21h-9A1.5 1.5 0 016 19.5v-15A1.5 1.5 0 017.5 3z" />
    <path d="M14 3v4h4M9 13h6M9 16.5h4" />
  </svg>
);

/** Premium paper-clip attach glyph (rounded, single stroke). */
const AttachIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l8.49-8.49a3.34 3.34 0 014.71 4.71l-8.49 8.49a1.67 1.67 0 01-2.36-2.36l7.78-7.78" />
  </svg>
);

/** Filled paper-plane — reads as premium on the gradient send button. */
const SendGlyph = ({ className = "h-[19px] w-[19px]" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a1 1 0 00-1.39 1.2L4.2 11.1a1 1 0 00.83.72l8.14 1.06a.12.12 0 010 .24l-8.14 1.06a1 1 0 00-.83.72l-2.19 6.3a1 1 0 001.39 1.2z" />
  </svg>
);

/** Round icon-button chrome shared by the attach / emoji / GIF triggers. */
const TRIGGER_BTN =
  "flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-200/70 hover:text-slate-700";

/** Staged-attachment preview shown above the input: review + caption, then send. */
function AttachmentPreview({ att, onClear }) {
  const { kind, url, name } = att;
  return (
    <div className="mb-2 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/90 p-2 pr-2">
      {kind === "image" ? (
        <img src={url} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
      ) : kind === "video" ? (
        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-black">
          <video src={url} muted preload="metadata" className="h-full w-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/55">
              <svg className="h-3.5 w-3.5 translate-x-[1px] text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5.5l12 6.5-12 6.5z" /></svg>
            </span>
          </span>
        </div>
      ) : kind === "audio" ? (
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 text-white shadow-sm">
          <AudioGlyph className="h-6 w-6" />
        </span>
      ) : (
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-sm">
          <DocGlyph className="h-6 w-6" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-bold text-slate-800">{name || kind}</span>
        <span className="block text-[11px] capitalize text-slate-400">{kind} · ready to send</span>
      </div>
      <button type="button" onClick={onClear} aria-label="Remove attachment" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-600">
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Message composer: reply/edit banner, attach menu, emoji + GIF pickers and an
 * auto-growing textarea. The whole bar is one focus-ring "pill" so it reads as a
 * single control rather than a row of loose buttons.
 */
export default function Composer({
  text,
  onType,
  onSend,
  sending,
  editing,
  onCancelEdit,
  replyTo,
  onCancelReply,
  onPickFile,
  pendingAttach,
  onClearAttach,
  uploading,
  onPickGif,
  onPaste,
  primary,
  otherName,
  locked,
  lockedText,
}) {
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const documentRef = useRef(null);
  const docRef = useRef(null);
  const attachBtnRef = useRef(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [dismissedUrl, setDismissedUrl] = useState("");

  // First URL in the draft → compose-time link preview (unless the user dismissed
  // it). Reset the dismissal when the input clears (e.g. after send).
  const detectedUrl = (text.match(URL_RE) || [])[0] || "";
  const showLinkPreview = !!detectedUrl && detectedUrl !== dismissedUrl && !editing && !pendingAttach;
  useEffect(() => {
    if (!text) setDismissedUrl("");
  }, [text]);

  // Auto-grow the textarea up to ~6 lines.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text]);

  // Focus the input when an edit or reply starts.
  useEffect(() => {
    if (editing || replyTo) taRef.current?.focus();
  }, [editing, replyTo]);

  if (locked) {
    return (
      <div className="shrink-0 border-t border-slate-100 bg-surface px-4 py-4">
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <rect x="5" y="11" width="14" height="9" rx="2" /><path strokeLinecap="round" d="M8 11V8a4 4 0 118 0v3" />
            </svg>
          </span>
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">{lockedText}</p>
        </div>
      </div>
    );
  }

  const banner = editing ? (
    <div className="flex items-center gap-2.5 border-t border-slate-100 bg-amber-50/70 dark:bg-amber-500/10 px-4 py-2">
      <EditIcon className="h-4 w-4 shrink-0 text-amber-500" />
      <div className="min-w-0 flex-1">
        <span className="block text-[11px] font-bold text-amber-700 dark:text-amber-300">Editing message</span>
        <span className="block truncate text-[11px] text-amber-600/80">{editing.body}</span>
      </div>
      <button type="button" onClick={onCancelEdit} aria-label="Cancel edit" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-amber-500 transition hover:bg-amber-100 dark:hover:bg-amber-500/15">
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  ) : replyTo ? (
    <div className="flex items-center gap-2.5 border-t border-slate-100 bg-slate-50 px-4 py-2">
      <span className="h-9 w-1 shrink-0 rounded-full" style={{ backgroundColor: primary }} />
      <ReplyIcon className="h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <span className="block text-[11px] font-bold" style={{ color: primary }}>
          Replying to {replyTo.is_me ? "yourself" : otherName}
        </span>
        {(() => {
          const { kind, text } = splitPreviewGlyph(replyTo.preview);
          return (
            <span className="flex items-center gap-1 truncate text-[11px] text-slate-500">
              <PreviewIcon kind={kind} className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate">{text}</span>
            </span>
          );
        })()}
      </div>
      <button type="button" onClick={onCancelReply} aria-label="Cancel reply" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200">
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  ) : null;

  const attachItem = (Icon, label, onClick, tone) => (
    <button
      type="button"
      onClick={() => {
        setAttachOpen(false);
        onClick();
      }}
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-slate-700 transition hover:bg-slate-100"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ backgroundColor: `${tone}18`, color: tone }}>
        <Icon className="h-4 w-4" />
      </span>
      {label}
    </button>
  );

  const canSend = (!!text.trim() || !!pendingAttach) && !sending;

  return (
    <div className="shrink-0">
      {banner}
      <div className="bg-gradient-to-b from-surface to-slate-50/60 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-5">
        {/* CHAT_COL keeps the input pill on the same rails as the bubbles. */}
        <div className={CHAT_COL}>
        {pendingAttach ? (
          <AttachmentPreview att={pendingAttach} onClear={onClearAttach} />
        ) : showLinkPreview ? (
          <LinkPreviewCard url={detectedUrl} onDismiss={() => setDismissedUrl(detectedUrl)} />
        ) : null}
        <div className="flex items-end gap-1 rounded-[26px] border border-slate-200/80 bg-surface py-1.5 pl-2 pr-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition focus-within:border-slate-300 focus-within:shadow-[0_10px_28px_-12px_rgba(15,23,42,0.28)]">
          {/* attach */}
          <div className="relative pb-0.5">
            <button
              ref={attachBtnRef}
              type="button"
              onClick={() => setAttachOpen((o) => !o)}
              disabled={!!editing}
              aria-label="Attach"
              className={`${TRIGGER_BTN} disabled:opacity-40 ${attachOpen ? "bg-slate-200/70 text-slate-700 rotate-45" : ""}`}
              style={attachOpen ? { color: primary } : undefined}
            >
              <AttachIcon className="h-5 w-5" />
            </button>
            <Popover
              open={attachOpen}
              onClose={() => setAttachOpen(false)}
              anchorRef={attachBtnRef}
              align="start"
              width={192}
            >
              {attachItem(PhotoIcon, "Photo", () => fileRef.current?.click(), "#22c55e")}
              {attachItem(VideoGlyph, "Video", () => videoRef.current?.click(), "#8b5cf6")}
              {attachItem(AudioGlyph, "Audio", () => audioRef.current?.click(), "#ec4899")}
              {attachItem(DocGlyph, "Document", () => documentRef.current?.click(), "#f59e0b")}
              {/* Camera capture — mobile only; still produces an image file. */}
              <span className="sm:hidden">{attachItem(CameraIcon, "Camera", () => docRef.current?.click(), "#6366f1")}</span>
            </Popover>
          </div>

          {/* emoji + gif — same round icon-button chrome as attach */}
          <div className="flex items-center gap-0.5 pb-0.5">
            <EmojiPicker onPick={(e) => onType({ target: { value: text + e } })} dropUp className={TRIGGER_BTN} />
            {!editing && <GifPicker onPick={onPickGif} dropUp className={`${TRIGGER_BTN} text-[10px] font-extrabold tracking-wide`} />}
            {uploading && (
              <span className="flex h-9 w-9 items-center justify-center">
                <Spinner size={16} />
              </span>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPickFile(f);
            }}
          />
          <input
            ref={videoRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPickFile(f);
            }}
          />
          <input
            ref={audioRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/mp4,audio/m4a,audio/aac,audio/ogg,audio/wav,audio/x-wav"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPickFile(f);
            }}
          />
          <input
            ref={documentRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.rar"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPickFile(f);
            }}
          />
          <input
            ref={docRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPickFile(f);
            }}
          />

          <textarea
            ref={taRef}
            rows={1}
            value={text}
            onChange={onType}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={pendingAttach ? "Add a caption…" : editing ? "Edit your message…" : `Message ${otherName || ""}…`}
            className="max-h-[132px] min-h-[36px] flex-1 resize-none self-center border-0 bg-transparent px-1.5 py-1.5 text-[14px] leading-snug text-slate-800 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0"
          />

          {/* send — filled paper-plane, brand gradient with a lift when armed */}
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            aria-label={editing ? "Save" : "Send"}
            className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 active:scale-90 disabled:cursor-not-allowed disabled:opacity-30 disabled:shadow-none"
            style={{
              ...brandGradient(primary),
              boxShadow: canSend ? `0 8px 18px -6px ${primary}80, inset 0 1px 0 rgba(255,255,255,0.25)` : undefined,
            }}
          >
            {editing ? <CheckIcon className="h-[18px] w-[18px]" width="2.4" /> : <SendGlyph className="h-[19px] w-[19px] translate-x-[1px]" />}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
